import {
  LogOut,
  // UserRound
} from "lucide-react";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";

import { useLogoutAdminMutation } from "@/api/admin/auth";
import { useLogoutRestaurantMutation } from "@/api/restaurant/auth";
import logo from "@/assets/images/logo.svg";
import SheetIcon from "@/assets/images/SheetIcon";
import { CustomButton } from "@/components/atoms";
import { toastSuccess } from "@/components/molecules";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getLogoutLoginPath, PATHS } from "@/config/path";
import { cn } from "@/lib/utils";
import { rootStore } from "@/store/RootStore";
import { commonIconProps16 } from "@/utils/constants";
import { COMPANY_NAME } from "@/utils/constants/defaultNames";
import { formatTestId } from "@/utils/formatTestId";

import { CountryFilter } from "../CountryFilter";
import { getManagementPath } from "./utils/getManagementPath";

// import NotificationPopover from "./NotificationPopover";
// import { getProfilePath } from "./utils/getProfilePath";

interface HeaderProps {
  children?: React.ReactNode;
}

const isTestingEnvironment =
  import.meta.env.VITE_SHOW_TESTING_BANNER === "true";

const Header: FC<HeaderProps> = observer(() => {
  const {
    isOnboarding,
    isAdmin,
    isRestaurant,
    userFullName,
    token,
    isBrandAccess,
    isGroupAccess,
  } = rootStore.authStore;
  const impersonation = rootStore.impersonation;

  const { pathname } = useLocation();
  const navigate = useNavigate();

  // TODO: hide until full integration

  const isManagement = [
    PATHS.RESTAURANT.GROUP.ROOT,
    PATHS.RESTAURANT.BRAND.ROOT,
  ].includes(pathname);

  // const profilePath = getProfilePath();
  const managementPath = getManagementPath();

  const dropMenuRestaurant = [
    // {
    //   key: "settings",
    //   title: "Profile settings",
    //   svg: <UserRound {...commonIconProps16} />,
    //   path: profilePath,
    // },
    {
      key: "logout",
      title: "Log out",
      svg: <LogOut {...commonIconProps16} />,
      path: "",
    },
  ];

  const { mutateAsync: adminLogout } = useLogoutAdminMutation();
  const { mutateAsync: restaurantLogout } = useLogoutRestaurantMutation();

  const handleActionMenu = async (key: string) => {
    if (key === "logout") {
      const accessToken = token;
      const wasAdmin = isAdmin;
      const wasRestaurant = isRestaurant;
      const loginPath = getLogoutLoginPath(wasAdmin, pathname);

      rootStore.authStore.startLogout();

      try {
        if (accessToken) {
          if (wasAdmin) {
            await adminLogout({ token: accessToken });
          } else if (wasRestaurant) {
            await restaurantLogout({ token: accessToken });
          }
        }

        toastSuccess("Logged out successfully!");
      } catch (error) {
        console.error("Logout API failed", error);
      } finally {
        navigate(loginPath, { replace: true });
        rootStore.authStore.clearSession();
        rootStore.restaurantAccessStore.reset();
        rootStore.adminAccessStore.reset();
      }
    }
  };

  return (
    <header
      className={cn(
        "bg-white md:static fixed top-0 left-0 right-0 z-50 shadow-xs md:shadow-none",
        impersonation.isImpersonating && "bg-[#FFF4D7]"
      )}
      data-testid="header"
    >
      {isAdmin && isTestingEnvironment && (
        <div
          aria-label="Testing environment"
          className="h-4 w-full bg-[repeating-linear-gradient(45deg,#f8d568_0,#f8d568_18px,#000_18px,#000_36px)]"
          data-testid="testing-environment-banner"
          role="status"
        />
      )}
      <div className="py-[6px] px-4">
        <div className="flex justify-between items-center">
          <div
            className={cn(
              "flex items-center gap-42",
              !matchPath({ path: getManagementPath(), end: true }, pathname) &&
                "pl-12 md:pl-0",
              !token && "pl-0"
            )}
          >
            <img
              src={logo}
              alt={`${COMPANY_NAME} logo`}
              data-testid="header-logo"
            />
            {impersonation.isImpersonating && (
              <div className="flex items-center gap-2">
                <SheetIcon />
                <span className="font-[Inter] text-xs text-[#292929] tracking-wide">
                  {impersonation.label}
                </span>
                <CustomButton
                  onClick={() => impersonation.clear()}
                  variant="link"
                  className="text-[#3B82F6] underline underline-offset-4 p-0"
                  size="xs"
                >
                  Switch to Admin
                </CustomButton>
              </div>
            )}
          </div>
          <NavigationMenu>
            <NavigationMenuList className="gap-1 sm:gap-3">
              {(isBrandAccess || isGroupAccess) && (
                <>
                  <NavigationMenuItem
                    data-testid="nav-business-management"
                    className="hidden md:block"
                  >
                    <Link
                      className={cn(
                        "text-gray-700 text-sm gap-x-1 [&>svg]:w-4 [&>svg]:h-4",
                        isManagement && "border-b border-gray-700 pb-[15px]"
                      )}
                      to={managementPath}
                    >
                      Business management
                    </Link>
                  </NavigationMenuItem>
                  {/* <NotificationPopover /> */}
                </>
              )}

              {isAdmin && token && !impersonation.isImpersonating && (
                <NavigationMenuItem data-testid="nav-country-filter">
                  <CountryFilter />
                </NavigationMenuItem>
              )}

              <NavigationMenuItem>
                <Popover>
                  <PopoverTrigger asChild>
                    <NavigationMenuTrigger
                      className={cn(
                        "text-gray-700 gap-x-1 [&>svg]:w-4 [&>svg]:h-4 pr-0! hover:bg-[#FFFFFF]! focus:bg-[#FFFFFF]! bg-transparent cursor-pointer",
                        token ? "[&>svg]:block" : "[&>svg]:hidden"
                      )}
                      data-testid="nav-user-trigger"
                    >
                      {isOnboarding && "English"}
                      {(isAdmin || isRestaurant) && userFullName}
                    </NavigationMenuTrigger>
                  </PopoverTrigger>

                  <PopoverContent
                    className="w-[200px] p-0"
                    sideOffset={-3}
                    align="end"
                  >
                    {(isRestaurant || isAdmin) && (
                      <>
                        {dropMenuRestaurant.map(elem => (
                          <NavigationMenuLink
                            asChild
                            key={elem.key}
                            className="flex items-center gap-2 py-[6px] px-3 hover:bg-accent focus:bg-accent"
                            data-testid={`nav-user-item-${formatTestId(elem.title)}`}
                          >
                            <Link
                              to={elem.path}
                              onClick={() => handleActionMenu(elem.key)}
                            >
                              {elem.svg}
                              <span className="text-sm">{elem.title}</span>
                            </Link>
                          </NavigationMenuLink>
                        ))}
                      </>
                    )}
                  </PopoverContent>
                </Popover>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>
      </div>
    </header>
  );
});

export default Header;
